import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { supabase } from '../../../lib/supabase';
import { toStorySlug } from '../../../lib/slugify';
import { useAuth } from '../../../hooks/useAuth';
import type { RankMode } from './RankingTabs';

export interface RankedStory {
  id: string;
  title: string;
  genre: string;
  content: string;
  cover_image: string | null;
  like_count: number;
  read_count: number;
  published_date: string;
  profiles: { display_name: string };
  _likeCount?: number;
  _commentCount?: number;
  _avgRating?: number | null;
}

interface Props {
  story: RankedStory;
  rank: number;
  mode: RankMode;
  animDelay?: number;
}

const RANK_COLORS: Record<number, string> = {
  1: 'bg-yellow-500 text-black',
  2: 'bg-gray-300 text-black',
  3: 'bg-amber-700 text-white',
};

const getReadTime = (content: string) => {
  const words = content.replace(/<[^>]*>/g, '').split(/\s+/).length;
  return `${Math.ceil(words / 200)} min`;
};

const getExcerpt = (content: string, max = 130) => {
  const plain = content.replace(/<[^>]*>/g, '');
  return plain.length > max ? plain.slice(0, max) + '...' : plain;
};

const RankedStoryCard = ({ story, rank, mode, animDelay = 0 }: Props) => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [likeCount, setLikeCount] = useState(story._likeCount ?? 0);
  const [commentCount] = useState(story._commentCount ?? 0);
  const [avgRating] = useState(story._avgRating ?? null);
  const [liked, setLiked] = useState(false);
  const [likeLoading, setLikeLoading] = useState(false);

  useEffect(() => {
    if (!user) { setLiked(false); return; }
    supabase
      .from('story_likes')
      .select('id')
      .eq('user_id', user.id)
      .eq('story_id', story.id)
      .maybeSingle()
      .then(({ data }) => setLiked(!!data));
  }, [user, story.id]);

  const handleLike = async (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (!user) { navigate('/auth/login'); return; }
    if (likeLoading) return;
    setLikeLoading(true);
    try {
      if (liked) {
        await supabase.from('story_likes').delete().eq('user_id', user.id).eq('story_id', story.id);
        setLiked(false);
        setLikeCount((p) => Math.max(0, p - 1));
      } else {
        await supabase.from('story_likes').insert({ user_id: user.id, story_id: story.id });
        setLiked(true);
        setLikeCount((p) => p + 1);
      }
    } catch (err) {
      console.error(err);
    } finally {
      setLikeLoading(false);
    }
  };

  const rankBadgeClass = RANK_COLORS[rank] ?? 'bg-red-900/60 text-gray-300';
  const isTop3 = rank <= 3;

  return (
    <Link
      to={`/story/${toStorySlug(story.title, story.id)}`}
      className={`group flex gap-4 bg-[#0f0f0f] border rounded-lg overflow-hidden transition-all duration-300 hover:shadow-2xl hover:shadow-red-900/20 cursor-pointer ${
        isTop3
          ? 'border-red-600/40 hover:border-red-500/70'
          : 'border-red-900/20 hover:border-red-600/40'
      }`}
      style={{ animation: `rankFadeIn 0.5s ease-out ${animDelay}s both` }}
    >
      {/* Rank column */}
      <div className="flex flex-col items-center justify-center px-4 py-5 shrink-0 min-w-[64px]">
        <div
          className={`w-10 h-10 flex items-center justify-center rounded-full font-bold text-sm font-['Cinzel'] shadow-md ${rankBadgeClass}`}
        >
          {rank <= 3 ? (
            <i className={rank === 1 ? 'ri-trophy-fill text-base' : rank === 2 ? 'ri-medal-fill text-base' : 'ri-award-fill text-base'} />
          ) : (
            `#${rank}`
          )}
        </div>
        {isTop3 && (
          <span className="text-xs text-gray-500 font-['Crimson_Text'] mt-1 whitespace-nowrap">
            #{rank}
          </span>
        )}
      </div>

      {/* Cover image */}
      <div className="relative w-28 h-full shrink-0 overflow-hidden">
        <img
          src={
            story.cover_image ||
            `https://public.readdy.ai/ai/img_res/953842cc5411eb54c011004667300385.jpg${story.id}&orientation=portrait`
          }
          alt={story.title}
          className="w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-110"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-transparent to-[#0f0f0f]/60" />
      </div>

      {/* Content */}
      <div className="flex flex-col justify-center py-4 pr-4 flex-1 min-w-0">
        <div className="flex items-center gap-2 mb-1.5">
          <span className="text-xs text-red-500 bg-red-600/10 px-2 py-0.5 rounded font-['Crimson_Text'] whitespace-nowrap">
            {story.genre}
          </span>
          <span className="text-xs text-gray-500 font-['Crimson_Text'] whitespace-nowrap">
            <i className="ri-time-line mr-1" />
            {getReadTime(story.content)}
          </span>
        </div>

        <h3 className="text-base font-bold text-white font-['Cinzel'] group-hover:text-red-500 transition-colors line-clamp-1 mb-1">
          {story.title}
        </h3>

        <p className="text-xs text-gray-500 font-['Crimson_Text'] mb-2">
          by <span className="text-red-500">{story.profiles.display_name}</span>
          <span className="mx-2 text-gray-700">·</span>
          {new Date(story.published_date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
        </p>

        <p className="text-gray-400 text-sm font-['Crimson_Text'] leading-relaxed line-clamp-2 hidden sm:block">
          {getExcerpt(story.content)}
        </p>

        {/* Stats */}
        <div className="flex items-center gap-4 mt-3">
          <button
            onClick={handleLike}
            className={`flex items-center gap-1 text-sm font-['Crimson_Text'] transition-colors cursor-pointer ${
              liked ? 'text-red-500' : 'text-gray-500 hover:text-red-500'
            } ${mode === 'liked' ? 'font-semibold' : ''}`}
          >
            <div className="w-4 h-4 flex items-center justify-center">
              <i className={liked ? 'ri-heart-fill' : 'ri-heart-line'} />
            </div>
            {likeCount.toLocaleString()}
          </button>

          <span className={`flex items-center gap-1 text-sm font-['Crimson_Text'] text-gray-500 ${mode === 'read' ? 'font-semibold text-orange-400' : ''}`}>
            <div className="w-4 h-4 flex items-center justify-center">
              <i className="ri-eye-line" />
            </div>
            {story.read_count.toLocaleString()}
          </span>

          <span className={`flex items-center gap-1 text-sm font-['Crimson_Text'] text-gray-500 ${mode === 'read' ? '' : ''}`}>
            <div className="w-4 h-4 flex items-center justify-center">
              <i className="ri-chat-3-line" />
            </div>
            {commentCount.toLocaleString()}
          </span>

          {avgRating !== null && (
            <span className={`flex items-center gap-1 text-sm font-['Crimson_Text'] ${mode === 'rated' ? 'text-yellow-400 font-semibold' : 'text-gray-500'}`}>
              <div className="w-4 h-4 flex items-center justify-center">
                <i className="ri-star-fill" />
              </div>
              {(avgRating as number).toFixed(1)}
            </span>
          )}
        </div>
      </div>

      {/* Arrow */}
      <div className="flex items-center pr-4 shrink-0">
        <div className="w-6 h-6 flex items-center justify-center text-red-600/50 group-hover:text-red-500 group-hover:translate-x-1 transition-all">
          <i className="ri-arrow-right-s-line text-xl" />
        </div>
      </div>
    </Link>
  );
};

export default RankedStoryCard;
